// Invite link edge calls (apps/edge/src/invites.ts). The token or code always travels in a POST
// body, so it never lands in a URL, a log line or the browser's history.
import { OkResponse } from "@proef/contract";
import type { z } from "zod";
import { call } from "../edgeApi";
import {
  AcceptInviteResponse,
  CreateInviteRequest,
  CreatedInvite,
  InviteList,
  InvitePreview,
  InviteView,
  type InviteKind,
  type InviteSecret,
} from "./invite";

function post(body: unknown): RequestInit {
  return { method: "POST", body: JSON.stringify(body) };
}

export type CreateInviteInput = z.input<typeof CreateInviteRequest>;

export function createInvite(req: CreateInviteInput): Promise<CreatedInvite> {
  return call(CreatedInvite, "/v1/invites", post(CreateInviteRequest.parse(req)));
}

export function listMyInvites(
  filter: { kind?: InviteKind; groupId?: string; sessionId?: string } = {},
): Promise<InviteList> {
  const params = new URLSearchParams({ mine: "1" });
  if (filter.kind !== undefined) params.set("kind", filter.kind);
  if (filter.groupId !== undefined) params.set("groupId", filter.groupId);
  if (filter.sessionId !== undefined) params.set("sessionId", filter.sessionId);
  return call(InviteList, `/v1/invites?${params.toString()}`);
}

export function revokeInvite(inviteId: string): Promise<InviteView> {
  return call(InviteView, `/v1/invites/${encodeURIComponent(inviteId)}/revoke`, post({}));
}

/** The link's maker takes someone who joined through it out again; doing it twice is harmless. */
export function removeRedemption(inviteId: string, userId: string): Promise<OkResponse> {
  return call(
    OkResponse,
    `/v1/invites/${encodeURIComponent(inviteId)}/redemptions/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

export function previewInvite(secret: InviteSecret): Promise<InvitePreview> {
  return call(InvitePreview, "/v1/invites/preview", post(secret));
}

export function acceptInvite(secret: InviteSecret): Promise<AcceptInviteResponse> {
  return call(AcceptInviteResponse, "/v1/invites/accept", post(secret));
}
